import { useMemo } from "react";

import { useTranslation } from "next-i18next";

import {
  HIW_BUYING_ROUTE,
  HIW_SELLING_ROUTE,
} from "@/common/static/routes.static";
import type { PhotoRefusalReason } from "@/features/open-home/model";

/**
 * Open Home copy, all six locales, from Lokalise (`open-home.json`, keys prefixed `oh-` because
 * key names are unique across the Lokalise project). What was uploaded is in
 * `i18n/open-home.source.json`; edit translations in Lokalise from now on.
 * No em dashes in web copy.
 */

export const OPEN_HOME_NAMESPACE = "open-home";

/** Open Home runs on all six sites (Thomas, 2026-10-03). */
export const OPEN_HOME_LOCALES = ["en", "nl", "de", "fr", "es", "it"] as const;

export const useOpenHomeCopy = () => {
  const { t } = useTranslation(OPEN_HOME_NAMESPACE);

  return useMemo(
    () => ({
      name: t("open-home:oh-name"),
      accountMenu: t("open-home:oh-account-menu"),
      faqTitle: t("open-home:oh-faq-title"),
      faqSub: t("open-home:oh-faq-sub"),
      // Listing first: how easy it is to open a home, then the buyer and safety answers
      // (Thomas, 10-09).
      faq: [
        { q: t("open-home:oh-faq-s1-q"), a: t("open-home:oh-faq-s1-a") },
        { q: t("open-home:oh-faq-s2-q"), a: t("open-home:oh-faq-s2-a") },
        { q: t("open-home:oh-faq-s3-q"), a: t("open-home:oh-faq-s3-a") },
        { q: t("open-home:oh-faq-s4-q"), a: t("open-home:oh-faq-s4-a") },
        { q: t("open-home:oh-faq-s5-q"), a: t("open-home:oh-faq-s5-a") },
        {
          q: t("open-home:oh-faq-q8"),
          a: t("open-home:oh-faq-a8"),
          link: {
            href: HIW_SELLING_ROUTE,
            label: t("open-home:oh-faq-link-selling"),
          },
        },
        { q: t("open-home:oh-faq-q1"), a: t("open-home:oh-faq-a1") },
        { q: t("open-home:oh-faq-q2"), a: t("open-home:oh-faq-a2") },
        { q: t("open-home:oh-faq-q3"), a: t("open-home:oh-faq-a3") },
        { q: t("open-home:oh-faq-q4"), a: t("open-home:oh-faq-a4") },
        { q: t("open-home:oh-faq-q5"), a: t("open-home:oh-faq-a5") },
        {
          q: t("open-home:oh-faq-q6"),
          a: t("open-home:oh-faq-a6"),
          link: {
            href: HIW_BUYING_ROUTE,
            label: t("open-home:oh-faq-link-buying"),
          },
        },
        { q: t("open-home:oh-faq-q7"), a: t("open-home:oh-faq-a7") },
      ],
      trust: [
        {
          title: t("open-home:oh-trust-payment"),
          body: t("open-home:oh-trust-payment-sub"),
        },
        {
          title: t("open-home:oh-trust-shipping"),
          body: t("open-home:oh-trust-shipping-sub"),
        },
        {
          title: t("open-home:oh-trust-private"),
          body: t("open-home:oh-trust-private-sub"),
        },
        {
          title: t("open-home:oh-trust-members"),
          body: t("open-home:oh-trust-members-sub"),
        },
      ],
      uploadPrivacy: t("open-home:oh-upload-privacy"),
      sellKeepTitle: t("open-home:oh-sell-keep-title"),
      sellKeepBody: t("open-home:oh-sell-keep-body"),
      minPublic: t("open-home:oh-min-public"),
      commissionLine: t("open-home:oh-commission-line"),
      recapLocation: t("open-home:oh-recap-location"),
      recapMembers: t("open-home:oh-recap-members"),
      recapBidFirst: t("open-home:oh-recap-bid-first"),
      previewBuyer: t("open-home:oh-preview-buyer"),
      priceSold: (low: string, high: string) =>
        t("open-home:oh-price-sold", { low, high }),
      priceConditionNudge: t("open-home:oh-price-condition-nudge"),
      roomForSale: (count: number) =>
        t("open-home:oh-room-for-sale", { count }),
      roomFrom: (amount: string) => t("open-home:oh-room-from", { amount }),
      // My Open Home sidebar and room header (redesign 10-10).
      yourRooms: t("open-home:oh-your-rooms"),
      roomToReview: (count: number) =>
        t("open-home:oh-room-to-review", { count }),
      roomNotVisible: t("open-home:oh-room-not-visible"),
      roomVisible: t("open-home:oh-room-visible"),
      roomHiddenHint: t("open-home:oh-room-hidden-hint"),
      viewAsMember: t("open-home:oh-view-as-member"),
      share: t("open-home:oh-share"),
      linkCopied: t("open-home:oh-link-copied"),
      dotMin: (amount: string) => t("open-home:oh-dot-min", { amount }),
      notTagged: t("open-home:oh-not-tagged"),
      itemContext: t("open-home:oh-item-context"),
      minReach: t("open-home:oh-min-reach"),
      minimumOfferLabel: t("open-home:oh-minimum-offer-label"),
      sameCheckoutStrong: t("open-home:oh-same-checkout-strong"),
      sameCheckoutRest: t("open-home:oh-same-checkout-rest"),
      iconShipping: t("open-home:oh-icon-shipping"),
      iconProtection: t("open-home:oh-icon-protection"),
      iconAddress: t("open-home:oh-icon-address"),
      // The key predates 10-27, when chatting before an offer was not possible; its text now
      // invites a question first.
      askFirst: t("open-home:oh-no-chat-title"),
      askOwner: t("open-home:oh-ask-owner"),
      offerChat: t("open-home:oh-offer-chat"),
      payLater: t("open-home:oh-pay-later"),
      quickMin: (amount: string) => t("open-home:oh-quick-min", { amount }),
      conceptBanner: t("open-home:oh-concept-banner"),
      conceptBannerHow: t("open-home:oh-concept-banner-how"),
      save: t("open-home:oh-save"),
      unsave: t("open-home:oh-unsave"),
      noRooms: t("open-home:oh-no-rooms"),
      noOtherRooms: t("open-home:oh-no-other-rooms"),
      yourHome: t("open-home:oh-your-home"),
      yourPiece: t("open-home:oh-your-piece"),
      brandUnsure: t("open-home:oh-brand-unsure"),
      brandUnsureSaved: t("open-home:oh-brand-unsure-saved"),
      loginTitle: t("open-home:oh-login-title"),
      loginBody: t("open-home:oh-login-body"),
      loginCta: t("open-home:oh-login-cta"),
      notConfigured: t("open-home:oh-not-configured"),
      location: t("open-home:oh-location"),
      viewRoom: t("open-home:oh-view-room"),
      noPrice: t("open-home:oh-no-price"),
      reserved: t("open-home:oh-reserved"),
      sold: t("open-home:oh-sold"),
      offerTitle: t("open-home:oh-offer-title"),
      offerAmount: t("open-home:oh-offer-amount"),
      minimumOffer: (amount: number) =>
        t("open-home:oh-minimum-offer", { amount }),
      offerBelowMinimum: (amount: number) =>
        t("open-home:oh-offer-below-minimum", { amount }),
      offerSend: t("open-home:oh-offer-send"),
      offerSent: t("open-home:oh-offer-sent"),
      attributes: t("open-home:oh-attributes"),
      condition: t("open-home:oh-condition"),
      conditionUnknown: t("open-home:oh-condition-unknown"),
      size: t("open-home:oh-size"),
      era: t("open-home:oh-era"),
      materials: t("open-home:oh-materials"),
      colors: t("open-home:oh-colors"),
      styles: t("open-home:oh-styles"),
      category: t("open-home:oh-category"),
      brand: t("open-home:oh-brand"),
      howItWorks: t("open-home:oh-how-it-works"),
      howSteps: [
        t("open-home:oh-how-step-1"),
        t("open-home:oh-how-step-2"),
        t("open-home:oh-how-step-3"),
        t("open-home:oh-how-step-4"),
      ],
      myHomeTitle: t("open-home:oh-my-home-title"),
      roomsUsed: (used: number, max: number) =>
        t("open-home:oh-rooms-used", { used, max }),
      newRoom: t("open-home:oh-new-room"),
      roomName: t("open-home:oh-room-name"),
      roomNamePlaceholder: t("open-home:oh-room-name-placeholder"),
      create: t("open-home:oh-create"),
      addPhotos: t("open-home:oh-add-photos"),
      photosHelp: t("open-home:oh-photos-help"),
      recognising: t("open-home:oh-recognising"),
      recognitionDone: (count: number) =>
        t("open-home:oh-recognition-done", { count }),
      reviewTitle: t("open-home:oh-review-title"),
      reviewHelp: t("open-home:oh-review-help"),
      forSale: t("open-home:oh-for-sale"),
      notForSale: t("open-home:oh-not-for-sale"),
      approve: t("open-home:oh-approve"),
      approved: t("open-home:oh-approved"),
      approveAll: t("open-home:oh-approve-all"),
      needsCheck: t("open-home:oh-needs-check"),
      looksLike: t("open-home:oh-looks-like"),
      isThisRight: t("open-home:oh-is-this-right"),
      yes: t("open-home:oh-yes"),
      no: t("open-home:oh-no"),
      typeBrand: t("open-home:oh-type-brand"),
      labelPhoto: t("open-home:oh-label-photo"),
      confidence: t("open-home:oh-confidence"),
      floor: t("open-home:oh-floor"),
      floorHelp: t("open-home:oh-floor-help"),
      floorHint: (amount: number) => t("open-home:oh-floor-hint", { amount }),
      publish: t("open-home:oh-publish"),
      unpublish: t("open-home:oh-unpublish"),
      published: t("open-home:oh-published"),
      draft: t("open-home:oh-draft"),
      statusLive: t("open-home:oh-status-live"),
      pause: t("open-home:oh-pause"),
      paused: t("open-home:oh-paused"),
      soldElsewhere: t("open-home:oh-sold-elsewhere"),
      deleteRoom: t("open-home:oh-delete-room"),
      openYourHome: t("open-home:oh-open-your-home"),
      deleteRoomConfirm: t("open-home:oh-delete-room-confirm"),
      removePhoto: t("open-home:oh-remove-photo"),
      // Seller privacy controls (2026-10-05).
      blurArea: t("open-home:oh-blur-area"),
      blurAreaHelp: t("open-home:oh-blur-area-help"),
      blurAreaSave: t("open-home:oh-blur-area-save"),
      blurCancel: t("open-home:oh-blur-cancel"),
      showBlurred: t("open-home:oh-show-blurred"),
      recheckPrivacy: t("open-home:oh-recheck-privacy"),
      recheckPrivacyDone: (count: number) =>
        t("open-home:oh-recheck-privacy-done", { count }),
      recheckPrivacyNone: t("open-home:oh-recheck-privacy-none"),
      // A part of the photo the privacy pass could not check (2026-10-07).
      photoNeedsPrivacyCheck: t("open-home:oh-photo-needs-privacy-check"),
      removePhotoConfirm: t("open-home:oh-remove-photo-confirm"),
      // Compact My home (2026-10-06).
      reviewNPieces: (count: number) =>
        t("open-home:oh-review-n-pieces", { count }),
      decided: (decided: number, total: number) =>
        t("open-home:oh-decided", { decided, total }),
      piecesTitle: t("open-home:oh-pieces-title"),
      piecesHelpShort: t("open-home:oh-pieces-help-short"),
      approveAllCount: (count: number) =>
        t("open-home:oh-approve-all-count", { count }),
      oneByOne: t("open-home:oh-one-by-one"),
      renameRoom: t("open-home:oh-rename-room"),
      publishConfirmTitle: (room: string) =>
        t("open-home:oh-publish-confirm-title", { room }),
      statusInReview: t("open-home:oh-status-in-review"),
      statusNoPhotos: t("open-home:oh-status-no-photos"),
      statusPaused: t("open-home:oh-status-paused"),
      moreActions: t("open-home:oh-more-actions"),
      emptyRoomTitle: t("open-home:oh-empty-room-title"),
      offersTitle: t("open-home:oh-offers-title"),
      accept: t("open-home:oh-accept"),
      decline: t("open-home:oh-decline"),
      counter: t("open-home:oh-counter"),
      counterAmount: t("open-home:oh-counter-amount"),
      statuses: {
        open: t("open-home:oh-status-open"),
        declined: t("open-home:oh-status-declined"),
        "auto-declined": t("open-home:oh-status-auto-declined"),
        countered: t("open-home:oh-status-countered"),
        accepted: t("open-home:oh-status-accepted"),
        "awaiting-details": t("open-home:oh-status-awaiting-details"),
        ready: t("open-home:oh-status-ready"),
        paid: t("open-home:oh-status-paid"),
        expired: t("open-home:oh-status-expired"),
        withdrawn: t("open-home:oh-status-withdrawn"),
      } as Record<string, string>,
      detailsTitle: t("open-home:oh-details-title"),
      detailsIntro: t("open-home:oh-details-intro"),
      width: t("open-home:oh-width"),
      depth: t("open-home:oh-depth"),
      height: t("open-home:oh-height"),
      weight: t("open-home:oh-weight"),
      pickupAllowed: t("open-home:oh-pickup-allowed"),
      courierAllowed: t("open-home:oh-courier-allowed"),
      conditionNote: t("open-home:oh-condition-note"),
      confirmSend: t("open-home:oh-confirm-send"),
      payout: (amount: string, fee: string, pct: string) =>
        t("open-home:oh-payout", { amount, fee, pct }),
      buyerWantsPickup: t("open-home:oh-buyer-wants-pickup"),
      pickup: t("open-home:oh-pickup"),
      courier: t("open-home:oh-courier"),
      reviewPieces: (count: number) =>
        t("open-home:oh-review-pieces", { count }),
      pieceOf: (current: number, total: number) =>
        t("open-home:oh-piece-of", { current, total }),
      keepIt: t("open-home:oh-keep-it"),
      sellIt: t("open-home:oh-sell-it"),
      detailsFound: t("open-home:oh-details-found"),
      tapToEdit: t("open-home:oh-tap-to-edit"),
      worthACheck: t("open-home:oh-worth-a-check"),
      looksRight: t("open-home:oh-looks-right"),
      skip: t("open-home:oh-skip"),
      saveNext: t("open-home:oh-save-next"),
      nextPiece: t("open-home:oh-next-piece"),
      optional: t("open-home:oh-optional"),
      privateOptional: t("open-home:oh-private-optional"),
      floorShort: t("open-home:oh-floor-short"),
      similarPrice: (amount: string) =>
        t("open-home:oh-similar-price", { amount }),
      use: t("open-home:oh-use"),
      priceRange: (low: string, high: string, count: number) =>
        t("open-home:oh-price-range", { low, high, count }),
      useAmount: (amount: string) => t("open-home:oh-use-amount", { amount }),
      readyPublish: t("open-home:oh-ready-publish"),
      summary: (sell: number, keep: number) =>
        t("open-home:oh-summary", { sell, keep }),
      summaryFloor: (count: number) =>
        t("open-home:oh-summary-floor", { count }),
      summaryPrivacy: t("open-home:oh-summary-privacy"),
      backToPieces: t("open-home:oh-back-to-pieces"),
      titleLabel: t("open-home:oh-title-label"),
      addValue: t("open-home:oh-add-value"),
      done: t("open-home:oh-done"),
      homeNew: t("open-home:oh-home-new"),
      homeTitle: t("open-home:oh-home-title"),
      homeBody: t("open-home:oh-home-body"),
      homeBodyShort: t("open-home:oh-home-body-short"),
      homeCta: t("open-home:oh-home-cta"),
      homeTrust: [
        t("open-home:oh-home-trust-1"),
        t("open-home:oh-home-trust-2"),
        t("open-home:oh-home-trust-3"),
      ],
      homeSampleTitle: t("open-home:oh-home-sample-title"),
      searchTabForSale: t("open-home:oh-search-tab-for-sale"),
      // Shop per room (Evelien's v2, agreed by Thomas 2026-10-06).
      roomTypes: {
        all: t("open-home:oh-room-type-all"),
        living: t("open-home:oh-room-type-living"),
        bedroom: t("open-home:oh-room-type-bedroom"),
        dining: t("open-home:oh-room-type-dining"),
        study: t("open-home:oh-room-type-study"),
        hallway: t("open-home:oh-room-type-hallway"),
        outdoor: t("open-home:oh-room-type-outdoor"),
        other: t("open-home:oh-room-type-other"),
      },
      shopIntro: t("open-home:oh-shop-intro"),
      shopHelpLink: t("open-home:oh-shop-help-link"),
      roomsCount: (count: number) =>
        count === 1
          ? t("open-home:oh-rooms-count-one")
          : t("open-home:oh-rooms-count-other", { count }),
      typeEmpty: t("open-home:oh-type-empty"),
      howtoTitle: t("open-home:oh-howto-title"),
      howtoSteps: [
        {
          title: t("open-home:oh-howto-step1-title"),
          body: t("open-home:oh-howto-step1-body"),
        },
        {
          title: t("open-home:oh-howto-step2-title"),
          body: t("open-home:oh-howto-step2-body"),
        },
        {
          title: t("open-home:oh-howto-step3-title"),
          body: t("open-home:oh-howto-step3-body"),
        },
      ],
      addRoom: t("open-home:oh-add-room"),
      showMoreRooms: t("open-home:oh-show-more-rooms"),
      roomsShown: (shown: number, total: number) =>
        t("open-home:oh-rooms-shown", { shown, total }),
      sellerCtaTitle: t("open-home:oh-seller-cta-title"),
      sellerCtaBody: t("open-home:oh-seller-cta-body"),
      piecesInRoom: (count: number) =>
        t("open-home:oh-pieces-in-room", { count }),
      moreRoomsLikeThis: t("open-home:oh-more-rooms-like-this"),
      moreRoomsFromSeller: t("open-home:oh-more-rooms-from-seller"),
      nextPhoto: t("common:common-next"),
      previousPhoto: t("common:common-previous"),
      seeSimilar: t("open-home:oh-see-similar"),
      fromRoom: t("open-home:oh-from-room"),
      allPieces: (count: number) => t("open-home:oh-all-pieces", { count }),
      fromThisRoom: t("open-home:oh-from-this-room"),
      youAreViewing: t("open-home:oh-you-are-viewing"),
      openRoomPage: t("open-home:oh-open-room-page"),
      bidsInChats: t("open-home:oh-bids-in-chats"),
      introBody: t("open-home:oh-intro-body"),
      introSteps: [
        {
          title: t("open-home:oh-intro-step1-title"),
          body: t("open-home:oh-intro-step1-body"),
        },
        {
          title: t("open-home:oh-intro-step2-title"),
          body: t("open-home:oh-intro-step2-body"),
        },
        {
          title: t("open-home:oh-intro-step3-title"),
          body: t("open-home:oh-intro-step3-body"),
        },
      ],
      introPrivacy: t("open-home:oh-intro-privacy"),
      entryTitle: t("open-home:oh-entry-title"),
      entryBody: t("open-home:oh-entry-body"),
      chooseRoom: t("open-home:oh-choose-room"),
      // Saved rooms, the Rooms tab in Favorites (gateway #999).
      saveRoom: t("open-home:oh-save-room"),
      roomSaved: t("open-home:oh-room-saved"),
      unsaveRoom: t("open-home:oh-unsave-room"),
      viewInFavorites: t("open-home:oh-view-in-favorites"),
      favoritesTabItems: (count: number) =>
        t("open-home:oh-favorites-tab-items", { count }),
      favoritesTabRooms: (count: number) =>
        t("open-home:oh-favorites-tab-rooms", { count }),
      savedRoomsEmpty: t("open-home:oh-saved-rooms-empty"),
      discoverMoreRooms: t("open-home:oh-discover-more-rooms"),
      piecesSold: (count: number) => t("open-home:oh-pieces-sold", { count }),
      // Price, offers, tiles, replace and move (Thomas, 2026-10-07).
      tilesHelp: t("open-home:oh-tiles-help"),
      tileToReview: t("open-home:oh-tile-to-review"),
      // Compact tiles (Thomas, 2026-10-10).
      tilesCta: (count: number) => t("open-home:oh-tiles-cta", { count }),
      tilesAllDone: t("open-home:oh-tiles-all-done"),
      tileEdit: t("open-home:oh-tile-edit"),
      tileClose: t("open-home:oh-tile-close"),
      termsMin: (amount: string) => t("open-home:oh-terms-min", { amount }),
      termsOffersOff: t("open-home:oh-terms-offers-off"),
      termsOpen: t("open-home:oh-terms-open"),
      confirmChanges: t("open-home:oh-confirm-changes"),
      cancelChanges: t("open-home:oh-cancel-changes"),
      finishEditingFirst: (title: string) =>
        t("open-home:oh-finish-editing-first", { title }),
      backToPiece: t("open-home:oh-back-to-piece"),
      priceLabel: t("open-home:oh-price-label"),
      priceHelp: t("open-home:oh-price-help"),
      offersLabel: t("open-home:oh-offers-label"),
      offersOn: t("open-home:oh-offers-on"),
      offersOff: t("open-home:oh-offers-off"),
      offersNeedPrice: t("open-home:oh-offers-need-price"),
      minAbovePrice: t("open-home:oh-min-above-price"),
      showPriceHint: t("open-home:oh-show-price-hint"),
      replacePhoto: t("open-home:oh-replace-photo"),
      replaceHelp: t("open-home:oh-replace-help"),
      replacingPhoto: t("open-home:oh-replacing-photo"),
      replaceDone: (found: number, added: number) =>
        t("open-home:oh-replace-done", { found, added }),
      // A photo the safety scan refused, per reason (Thomas, 2026-10-27).
      photoRefused: {
        sexual: t("open-home:oh-photo-refused-sexual"),
        violence: t("open-home:oh-photo-refused-violence"),
        hate: t("open-home:oh-photo-refused-hate"),
        weapons: t("open-home:oh-photo-refused-weapons"),
        drugs: t("open-home:oh-photo-refused-drugs"),
        illegal_goods: t("open-home:oh-photo-refused-illegal-goods"),
        prompt_injection: t("open-home:oh-photo-refused-prompt-injection"),
        unsafe: t("open-home:oh-photo-refused-unsafe"),
      } satisfies Record<PhotoRefusalReason, string>,
      photoRefusedMistake: t("open-home:oh-photo-refused-mistake"),
      missingTitle: t("open-home:oh-missing-title"),
      missingHelp: t("open-home:oh-missing-help"),
      keepListing: t("open-home:oh-keep-listing"),
      removeListing: t("open-home:oh-remove-listing"),
      hasOfferStays: t("open-home:oh-has-offer-stays"),
      movePhoto: t("open-home:oh-move-photo"),
      movePhotoHelp: t("open-home:oh-move-photo-help"),
      newRoomOption: t("open-home:oh-new-room-option"),
      move: t("open-home:oh-move"),
      buyFor: (amount: string) => t("open-home:oh-buy-for", { amount }),
      priceBuyer: t("open-home:oh-price-buyer"),
      orOffer: t("open-home:oh-or-offer"),
      onlyAtPrice: t("open-home:oh-only-at-price"),
      boughtReserved: t("open-home:oh-bought-reserved"),
      buyHow: t("open-home:oh-buy-how"),
      // Pieces list and edit panel (Thomas, 2026-10-10).
      filterAll: t("open-home:oh-filter-all"),
      filterLabel: t("open-home:oh-filter-label"),
      filterEmpty: t("open-home:oh-filter-empty"),
      objectType: t("open-home:oh-object-type"),
      categoryPath: t("open-home:oh-category-path"),
      designer: t("open-home:oh-designer"),
      model: t("open-home:oh-model"),
    }),
    [t]
  );
};

export type OpenHomeCopy = ReturnType<typeof useOpenHomeCopy>;
